import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus, X } from 'lucide-react';

function CounterRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="sw-counter">
      <div className="sw-counter__info">
        <span className="sw-counter__label">{label}</span>
        {hint && <span className="sw-counter__hint">{hint}</span>}
      </div>
      <div className="sw-counter__controls">
        <button
          type="button"
          className="sw-counter__btn"
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
          aria-label={`Decrease ${label}`}
        >
          <Minus size={16} />
        </button>
        <span className="sw-counter__value" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="sw-counter__btn"
          disabled={value >= max}
          onClick={() => onChange(value + 1)}
          aria-label={`Increase ${label}`}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
}

export default function TravellerPickerModal({
  open,
  title,
  variant,
  values,
  onClose,
  onApply,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !values) return null;

  const patch = (partial) => onApply({ ...values, ...partial });

  const totalPax = (values.adults || 0) + (values.children || 0);
  const maxTotal = 9;

  const clampPax = (nextAdults, nextChildren) => {
    let adults = Math.max(1, nextAdults);
    let children = Math.max(0, nextChildren);
    while (adults + children > maxTotal) {
      if (children > 0) children -= 1;
      else adults -= 1;
    }
    return { adults, children };
  };

  let body = null;

  if (variant === 'flight') {
    body = (
      <>
        <CounterRow
          label="Adults"
          hint="12+ years"
          value={values.adults}
          min={1}
          max={maxTotal - values.children}
          onChange={(n) => patch(clampPax(n, values.children))}
        />
        <CounterRow
          label="Children"
          hint="2–11 years"
          value={values.children}
          min={0}
          max={maxTotal - values.adults}
          onChange={(n) => patch(clampPax(values.adults, n))}
        />
        <div className="sw-picker-field">
          <label htmlFor="sw-cabin">Cabin class</label>
          <select
            id="sw-cabin"
            value={values.cabin}
            onChange={(e) => patch({ cabin: e.target.value })}
          >
            <option value="economy">Economy</option>
            <option value="premium">Premium Economy</option>
            <option value="business">Business</option>
            <option value="first">First</option>
          </select>
        </div>
      </>
    );
  } else if (variant === 'hotel') {
    body = (
      <>
        <CounterRow
          label="Adults"
          hint="18+ years"
          value={values.adults}
          min={1}
          max={6}
          onChange={(n) => patch({ adults: n })}
        />
        <CounterRow
          label="Children"
          hint="0–17 years"
          value={values.children}
          min={0}
          max={4}
          onChange={(n) => patch({ children: n })}
        />
        <CounterRow
          label="Rooms"
          value={values.rooms}
          min={1}
          max={4}
          onChange={(n) => patch({ rooms: n })}
        />
      </>
    );
  } else if (variant === 'car') {
    body = (
      <div className="sw-picker-field">
        <label htmlFor="sw-car-type">Car type</label>
        <select
          id="sw-car-type"
          value={values.carType}
          onChange={(e) => patch({ carType: e.target.value })}
        >
          <option value="any">Any type</option>
          <option value="Economy">Economy</option>
          <option value="SUV">SUV</option>
          <option value="Luxury">Luxury</option>
          <option value="Van">Van</option>
        </select>
      </div>
    );
  } else {
    body = (
      <>
        <CounterRow
          label="Adults"
          hint="12+ years"
          value={values.adults}
          min={1}
          max={maxTotal - values.children}
          onChange={(n) => patch(clampPax(n, values.children))}
        />
        <CounterRow
          label="Children"
          hint="2–11 years"
          value={values.children}
          min={0}
          max={maxTotal - values.adults}
          onChange={(n) => patch(clampPax(values.adults, n))}
        />
      </>
    );
  }

  const summary =
    variant === 'car'
      ? values.carType === 'any'
        ? 'Any car type'
        : values.carType
      : variant === 'hotel'
        ? `${values.adults + values.children} guest${values.adults + values.children !== 1 ? 's' : ''}, ${values.rooms} room${values.rooms !== 1 ? 's' : ''}`
        : `${totalPax} traveller${totalPax !== 1 ? 's' : ''}`;

  return createPortal(
    <div className="sw-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="sw-modal sw-modal--travellers"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sw-traveller-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sw-modal__head">
          <h3 id="sw-traveller-modal-title">{title}</h3>
          <button type="button" className="sw-modal__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="sw-modal__body">{body}</div>

        <footer className="sw-modal__foot">
          <p className="sw-modal__summary">{summary}</p>
          <button type="button" className="btn-gold sw-modal__btn" onClick={onClose}>
            Done
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
